/**
 * ProseMirror suggestion plugin for note @-mentions.
 *
 * Tracks an active `@<query>` range, fires open/update/close callbacks, and
 * exposes a `selectMention` command that replaces the range with a
 * link-marked text node. Designed to be installed by Milkdown via
 * `$prose(() => createMentionPlugin(callbacks))`, but works against any
 * CommonMark-compatible schema with `link` mark + `code`/`code_block`.
 */
import { Plugin, PluginKey, type EditorState, type Transaction } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";
import type { Node as PMNode } from "prosemirror-model";

import { entityHref } from "../entityHref";

export interface MentionPluginCallbacks {
  onOpen(query: string, caretRect: DOMRect): void;
  onUpdate(query: string, caretRect: DOMRect): void;
  onClose(): void;
}

export interface MentionSelection {
  readonly schemaId: string;
  readonly entityId: string;
  readonly displayName: string;
}

export interface MentionState {
  readonly active: boolean;
  readonly from: number;
  readonly to: number;
  readonly query: string;
}

interface OpenMeta {
  readonly type: "open";
  readonly from: number;
}
interface CloseMeta {
  readonly type: "close";
}
type Meta = OpenMeta | CloseMeta;

export const mentionPluginKey = new PluginKey<MentionState | null>("magnis-mention");

const INACTIVE: MentionState | null = null;

function isWhitespaceOrPunctuation(ch: string | null): boolean {
  if (ch === null) return true; // start of paragraph
  return /[\s\p{P}]/u.test(ch);
}

function isWordBoundary(doc: PMNode, pos: number): boolean {
  if (pos === 0) return true;
  const $pos = doc.resolve(pos);
  if ($pos.parentOffset === 0) return true;
  const before = doc.textBetween(pos - 1, pos, "\n", "\n");
  return isWhitespaceOrPunctuation(before || null);
}

function isInsideCodeOrLink(state: EditorState, pos: number): boolean {
  const { doc, schema } = state;
  const $pos = doc.resolve(pos);
  if ($pos.parent.type.spec.code) return true;
  if ($pos.parent.type.name === "code_block") return true;

  const linkType = schema.marks.link;
  const codeType = schema.marks.code;
  const marksAt = $pos.marks();
  if (marksAt.some((m) => m.type === linkType || m.type === codeType)) return true;

  const stored = state.storedMarks;
  if (stored?.some((m) => m.type === linkType || m.type === codeType)) return true;
  return false;
}

function rangeStillStartsWithAt(doc: PMNode, from: number): boolean {
  if (from < 0 || from >= doc.content.size) return false;
  return doc.textBetween(from, from + 1, "\n", "\n") === "@";
}

export function createMentionPlugin(
  callbacks: MentionPluginCallbacks,
): Plugin<MentionState | null> {
  // Trigger candidate captured by handleTextInput; consumed by appendTransaction.
  let pendingTrigger: { from: number } | null = null;

  return new Plugin<MentionState | null>({
    key: mentionPluginKey,

    state: {
      init: (): MentionState | null => INACTIVE,
      apply: (tr, value, _oldState, newState): MentionState | null => {
        const meta = tr.getMeta(mentionPluginKey) as Meta | undefined;
        if (meta) {
          if (meta.type === "open") {
            return { active: true, from: meta.from, to: meta.from + 1, query: "" };
          }
          // meta.type === "close" — the only other variant.
          return INACTIVE;
        }

        if (!value?.active) return value;

        const newFrom = tr.mapping.map(value.from, -1);
        const newTo = tr.mapping.map(value.to, 1);

        if (!rangeStillStartsWithAt(newState.doc, newFrom)) {
          return INACTIVE;
        }
        const query = newState.doc.textBetween(newFrom + 1, newTo, "\n", "\n");
        if (/[\s\n]/.test(query)) {
          return INACTIVE;
        }
        return { active: true, from: newFrom, to: newTo, query };
      },
    },

    props: {
      handleTextInput: (view, from, _to, text): boolean => {
        if (text !== "@") return false;
        const state = mentionPluginKey.getState(view.state);
        if (state?.active) return false;
        if (!isWordBoundary(view.state.doc, from)) return false;
        if (isInsideCodeOrLink(view.state, from)) return false;
        pendingTrigger = { from };
        return false;
      },
    },

    appendTransaction: (_trs, _oldState, newState): Transaction | null => {
      if (!pendingTrigger) return null;
      const trigger = pendingTrigger;
      pendingTrigger = null;
      if (!rangeStillStartsWithAt(newState.doc, trigger.from)) return null;
      return newState.tr.setMeta(mentionPluginKey, {
        type: "open",
        from: trigger.from,
      } satisfies Meta);
    },

    view: (view): { update: (v: EditorView, ps: EditorState) => void; destroy: () => void } => {
      let prev = mentionPluginKey.getState(view.state);
      return {
        update: (updatedView, _prevState): void => {
          const next = mentionPluginKey.getState(updatedView.state);
          const prevActive = prev?.active === true ? prev : null;
          const nextActive = next?.active === true ? next : null;
          if (!prevActive && nextActive) {
            const rect = updatedView.coordsAtPos(nextActive.to);
            callbacks.onOpen(nextActive.query, coordsToRect(rect));
          } else if (prevActive && nextActive) {
            if (
              nextActive.query !== prevActive.query ||
              nextActive.to !== prevActive.to
            ) {
              const rect = updatedView.coordsAtPos(nextActive.to);
              callbacks.onUpdate(nextActive.query, coordsToRect(rect));
            }
          } else if (prevActive && !nextActive) {
            callbacks.onClose();
          }
          prev = next;
        },
        destroy: (): void => undefined,
      };
    },
  });
}

function coordsToRect(c: { left: number; top: number; right: number; bottom: number }): DOMRect {
  const width = Math.max(0, c.right - c.left);
  const height = Math.max(0, c.bottom - c.top);
  return {
    left: c.left,
    top: c.top,
    right: c.right,
    bottom: c.bottom,
    x: c.left,
    y: c.top,
    width,
    height,
    toJSON: () => ({
      left: c.left,
      top: c.top,
      right: c.right,
      bottom: c.bottom,
      width,
      height,
    }),
  } as DOMRect;
}

/**
 * Replace the active mention range with a link-marked text node and a
 * trailing space. Returns false when the plugin is inactive OR
 * `entityHref` cannot resolve the schema/id pair.
 */
export function selectMention(view: EditorView, item: MentionSelection): boolean {
  const state = mentionPluginKey.getState(view.state);
  if (!state?.active) return false;

  const href = entityHref(item.schemaId, item.entityId);
  if (!href) {
    console.info("[mention] unmapped schema:", item.schemaId);
    view.dispatch(view.state.tr.setMeta(mentionPluginKey, { type: "close" } satisfies Meta));
    return false;
  }

  const { schema } = view.state;
  const linkMarkType = schema.marks.link;
  if (!linkMarkType) {
    console.info("[mention] schema has no link mark");
    view.dispatch(view.state.tr.setMeta(mentionPluginKey, { type: "close" } satisfies Meta));
    return false;
  }
  // The popup row + the chip-style icon (CSS `::before`) already signal
  // that this is an entity mention; the literal `@` is dropped from the
  // visible text so the chip reads cleanly as the entity's display name.
  const linkMark = linkMarkType.create({ href, title: null });
  const textNode = schema.text(item.displayName, [linkMark]);

  const tr = view.state.tr
    .replaceWith(state.from, state.to, textNode)
    .insertText(" ")
    // Clear the link mark from storedMarks so subsequent keystrokes don't
    // extend the chip — without this, "Hello @AlexPark tail" would render
    // "tail" as part of the link.
    .setStoredMarks([])
    .setMeta(mentionPluginKey, { type: "close" } satisfies Meta);
  view.dispatch(tr);
  return true;
}
